// =============================================================================
// VINUM - Image Crop Dialog
// Modale di crop manuale per le upload immagine admin (catalogo, cantine,
// settings, notifiche). Permette di trascinare un rettangolo sull'anteprima
// per scegliere quale porzione dell'immagine salvare.
//
// Niente dipendenze esterne (no react-image-crop): rendering DOM + canvas
// per il risultato finale. ~300 righe, dark-mode aware, mobile friendly.
// =============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter } from './Modal';
import { Button } from './Button';
import { cn } from '@/lib/cn';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface CroppedImageResult {
  /** base64 (senza prefisso data:...,) pronto per /media/user */
  base64: string;
  /** mimeType del file ritagliato (sempre image/jpeg per ridurre la size) */
  mimeType: string;
  /** filename originale (utile per l'upload) */
  filename: string;
  /** dimensione finale in bytes (stima dal base64) */
  size: number;
}

export interface ImageCropDialogProps {
  open: boolean;
  /** File originale scelto dall'utente. Quando cambia, la modale si reset. */
  file: File | null;
  onClose: () => void;
  onConfirm: (result: CroppedImageResult) => void | Promise<void>;
  /**
   * Aspect ratio del crop. undefined = libero (default).
   * Es. 1 = quadrato, 16/9, 4/3...
   */
  aspectRatio?: number;
  /** Dimensione max output (lato piu' lungo). Default 1600px. */
  maxOutputSize?: number;
  /** JPEG quality 0-1. Default 0.9. */
  quality?: number;
  /** Loading state esterno (durante l'upload post-confirm). */
  loading?: boolean;
  title?: string;
}

interface CropRect {
  // Coordinate normalizzate (0-1) relative all'immagine sorgente
  x: number;
  y: number;
  w: number;
  h: number;
}

// -----------------------------------------------------------------------------
// Hook: gestione drag/resize del rettangolo di crop
// -----------------------------------------------------------------------------

type DragMode =
  | { kind: 'move'; startMouseX: number; startMouseY: number; startRect: CropRect }
  | { kind: 'resize'; corner: 'nw' | 'ne' | 'sw' | 'se'; startMouseX: number; startMouseY: number; startRect: CropRect }
  | null;

// -----------------------------------------------------------------------------
// Componente principale
// -----------------------------------------------------------------------------

export const ImageCropDialog: React.FC<ImageCropDialogProps> = ({
  open,
  file,
  onClose,
  onConfirm,
  aspectRatio,
  maxOutputSize = 1600,
  quality = 0.9,
  loading = false,
  title = "Ritaglia l'immagine",
}) => {
  // ImageBitmap-like via HTMLImageElement (piu' compatibile)
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [imgNatural, setImgNatural] = useState<{ w: number; h: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  // Crop rect in coordinate normalizzate (0-1)
  const [crop, setCrop] = useState<CropRect>({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
  const dragRef = useRef<DragMode>(null);

  // -----------------------------------------------------------------------
  // Carica il file in object URL quando arriva
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!file || !open) {
      setImgSrc(null);
      setImgNatural(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setImgSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file, open]);

  // Reset crop quando cambia l'immagine
  const handleImgLoaded = () => {
    const el = imgRef.current;
    if (!el) return;
    setImgNatural({ w: el.naturalWidth, h: el.naturalHeight });
    // Crop iniziale: 80% al centro, con aspect ratio se richiesto
    if (aspectRatio && aspectRatio > 0) {
      const imgAR = el.naturalWidth / el.naturalHeight;
      let w: number;
      let h: number;
      if (aspectRatio > imgAR) {
        w = 0.9;
        h = (w * imgAR) / aspectRatio;
      } else {
        h = 0.9;
        w = (h * aspectRatio) / imgAR;
      }
      setCrop({ x: (1 - w) / 2, y: (1 - h) / 2, w, h });
    } else {
      setCrop({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
    }
  };

  // -----------------------------------------------------------------------
  // Misura il container per layout overlay del crop
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const el = containerRef.current;
      const img = imgRef.current;
      if (!el || !img) return;
      // Useremo le dimensioni renderizzate dell'<img> (object-contain).
      setContainerSize({ w: img.clientWidth, h: img.clientHeight });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (imgRef.current) ro.observe(imgRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [open, imgSrc]);

  // -----------------------------------------------------------------------
  // Drag handlers
  // -----------------------------------------------------------------------
  const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

  const applyAspectFromCorner = useCallback(
    (rect: CropRect, corner: 'nw' | 'ne' | 'sw' | 'se'): CropRect => {
      if (!aspectRatio || !imgNatural) return rect;
      // Coordinate in pixel naturali per applicare l'AR
      const pxW = rect.w * imgNatural.w;
      const targetPxH = pxW / aspectRatio;
      // Adatta l'altezza per matchare aspectRatio, dal corner
      const newH = targetPxH / imgNatural.h;
      let newY = rect.y;
      if (corner === 'nw' || corner === 'ne') {
        newY = rect.y + rect.h - newH;
      }
      return {
        x: rect.x,
        y: clamp01(newY),
        w: rect.w,
        h: Math.min(newH, 1 - newY),
      };
    },
    [aspectRatio, imgNatural],
  );

  const onPointerDownOverlay = (e: React.PointerEvent, mode: DragMode) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = mode;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current || !containerSize.w || !containerSize.h) return;
    const dxNorm = (e.movementX || 0) / containerSize.w;
    const dyNorm = (e.movementY || 0) / containerSize.h;

    setCrop((prev) => {
      const m = dragRef.current!;
      if (m.kind === 'move') {
        const x = clamp01(prev.x + dxNorm);
        const y = clamp01(prev.y + dyNorm);
        return {
          x: Math.min(x, 1 - prev.w),
          y: Math.min(y, 1 - prev.h),
          w: prev.w,
          h: prev.h,
        };
      }
      // resize
      let { x, y, w, h } = prev;
      const corner = m.corner;
      if (corner.includes('e')) w = clamp01(w + dxNorm);
      if (corner.includes('s')) h = clamp01(h + dyNorm);
      if (corner.includes('w')) {
        const newX = clamp01(x + dxNorm);
        w = w + (x - newX);
        x = newX;
      }
      if (corner.includes('n')) {
        const newY = clamp01(y + dyNorm);
        h = h + (y - newY);
        y = newY;
      }
      // Minimum size: 5% di immagine
      if (w < 0.05) w = 0.05;
      if (h < 0.05) h = 0.05;
      // Clamp ai bordi
      if (x + w > 1) w = 1 - x;
      if (y + h > 1) h = 1 - y;
      let rect: CropRect = { x, y, w, h };
      if (aspectRatio) rect = applyAspectFromCorner(rect, corner);
      return rect;
    });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  // -----------------------------------------------------------------------
  // Conferma: ritaglia su canvas e ritorna base64
  // -----------------------------------------------------------------------
  const handleConfirm = async () => {
    if (!file || !imgRef.current || !imgNatural) return;
    const img = imgRef.current;
    // Pixel coords del crop sull'immagine sorgente
    const sx = Math.round(crop.x * imgNatural.w);
    const sy = Math.round(crop.y * imgNatural.h);
    const sw = Math.round(crop.w * imgNatural.w);
    const sh = Math.round(crop.h * imgNatural.h);

    // Output: ridimensiona se piu' grande di maxOutputSize sul lato lungo
    let outW = sw;
    let outH = sh;
    const longest = Math.max(sw, sh);
    if (longest > maxOutputSize) {
      const scale = maxOutputSize / longest;
      outW = Math.round(sw * scale);
      outH = Math.round(sh * scale);
    }

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Sfondo bianco (sicurezza per immagini con alpha esportate in JPEG)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    const base64 = dataUrl.split(',')[1] || '';
    // Stima dimensione binaria dal base64
    const size = Math.floor((base64.length * 3) / 4);

    await onConfirm({
      base64,
      mimeType: 'image/jpeg',
      filename: file.name.replace(/\.[^.]+$/, '') + '.jpg',
      size,
    });
  };

  // -----------------------------------------------------------------------
  // Rendering overlay crop (in coordinate del container)
  // -----------------------------------------------------------------------
  const overlayStyle = useMemo<React.CSSProperties>(() => {
    if (!containerSize.w || !containerSize.h) return { display: 'none' };
    return {
      position: 'absolute',
      left: `${crop.x * containerSize.w}px`,
      top: `${crop.y * containerSize.h}px`,
      width: `${crop.w * containerSize.w}px`,
      height: `${crop.h * containerSize.h}px`,
    };
  }, [crop, containerSize]);

  // Le 4 "ombre" intorno al crop per dare l'effetto dim
  const dimStyle = (side: 'top' | 'bottom' | 'left' | 'right'): React.CSSProperties => {
    if (!containerSize.w || !containerSize.h) return { display: 'none' };
    const px = {
      x: crop.x * containerSize.w,
      y: crop.y * containerSize.h,
      w: crop.w * containerSize.w,
      h: crop.h * containerSize.h,
    };
    const base: React.CSSProperties = {
      position: 'absolute',
      background: 'rgba(0,0,0,0.55)',
      pointerEvents: 'none',
    };
    switch (side) {
      case 'top':
        return { ...base, left: 0, top: 0, width: '100%', height: `${px.y}px` };
      case 'bottom':
        return { ...base, left: 0, top: `${px.y + px.h}px`, width: '100%', bottom: 0 };
      case 'left':
        return { ...base, left: 0, top: `${px.y}px`, width: `${px.x}px`, height: `${px.h}px` };
      case 'right':
        return { ...base, left: `${px.x + px.w}px`, top: `${px.y}px`, right: 0, height: `${px.h}px` };
    }
  };

  const cornerCls =
    'absolute w-3 h-3 bg-white border-2 border-wine-600 rounded-sm pointer-events-auto';

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      closeOnOverlay={!loading}
      closeOnEscape={!loading}
    >
      <ModalBody>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{title}</h2>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-3">
          Trascina il rettangolo per scegliere l'area visibile.
          {aspectRatio ? ' Le proporzioni sono fissate.' : ' Trascina gli angoli per ridimensionare.'}
        </p>
        <div
          ref={containerRef}
          className="relative w-full bg-gray-100 dark:bg-slate-900 rounded-lg overflow-hidden flex items-center justify-center select-none"
          style={{ maxHeight: '60vh' }}
        >
          {imgSrc && (
            <img
              ref={imgRef}
              src={imgSrc}
              alt="Da ritagliare"
              onLoad={handleImgLoaded}
              className="block max-h-[60vh] max-w-full object-contain"
              draggable={false}
            />
          )}
          {/* Overlay dim aree esterne al crop */}
          <div style={dimStyle('top')} />
          <div style={dimStyle('bottom')} />
          <div style={dimStyle('left')} />
          <div style={dimStyle('right')} />
          {/* Rettangolo crop */}
          <div
            style={overlayStyle}
            className="border-2 border-wine-500 cursor-move touch-none"
            onPointerDown={(e) =>
              onPointerDownOverlay(e, {
                kind: 'move',
                startMouseX: e.clientX,
                startMouseY: e.clientY,
                startRect: { ...crop },
              })
            }
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {/* Corner handles */}
            {(['nw', 'ne', 'sw', 'se'] as const).map((c) => (
              <div
                key={c}
                className={cn(
                  cornerCls,
                  c === 'nw' && '-top-1.5 -left-1.5 cursor-nwse-resize',
                  c === 'ne' && '-top-1.5 -right-1.5 cursor-nesw-resize',
                  c === 'sw' && '-bottom-1.5 -left-1.5 cursor-nesw-resize',
                  c === 'se' && '-bottom-1.5 -right-1.5 cursor-nwse-resize',
                )}
                onPointerDown={(e) =>
                  onPointerDownOverlay(e, {
                    kind: 'resize',
                    corner: c,
                    startMouseX: e.clientX,
                    startMouseY: e.clientY,
                    startRect: { ...crop },
                  })
                }
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              />
            ))}
          </div>
        </div>
        {imgNatural && (
          <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">
            Dimensione originale: {imgNatural.w}×{imgNatural.h}px · Area ritagliata:{' '}
            {Math.round(crop.w * imgNatural.w)}×{Math.round(crop.h * imgNatural.h)}px
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          Annulla
        </Button>
        <Button variant="primary" onClick={handleConfirm} loading={loading} disabled={!imgNatural}>
          Conferma e carica
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ImageCropDialog.displayName = 'ImageCropDialog';
